import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowUpRight, RotateCcw, Check } from "lucide-react";
import Seo from "@/components/Seo";

/**
 * A tiny 3-question membership recommendation quiz.
 * Each answer carries a weighting toward "access" or "coached".
 * The higher total wins; ties tilt toward Coached (higher-support default).
 */
const QUESTIONS = [
  {
    q: "When you're training, what do you actually want?",
    answers: [
      { label: "Someone telling me what to do — I want a plan.", weight: "coached" },
      { label: "The space and kit to do my own thing.", weight: "access" },
    ],
  },
  {
    q: "What's the bigger obstacle for you right now?",
    answers: [
      { label: "I keep stopping and starting. I need accountability.", weight: "coached" },
      { label: "I just want a calm, welcoming gym near home.", weight: "access" },
    ],
  },
  {
    q: "Which of these sounds more like you?",
    answers: [
      { label: "I want structure, coaching and someone in my corner.", weight: "coached" },
      { label: "I've trained for years — I just need great facilities.", weight: "access" },
    ],
  },
];

const RESULTS = {
  coached: {
    slug: "kore-coached",
    name: "Kore Coaching",
    price: "£149 per month",
    tag: "Most support",
    lead: "Based on your answers, Kore Coaching is the honest recommendation.",
    body: "You want structure and accountability — and Kore Coaching is built around exactly that. Two coached small-group sessions a week, a written training programme, monthly check-ins and a coach who genuinely knows what you're working on.",
    offer: { label: "Start your 30 Day Trial · £99", to: "/30-day-trial" },
    primary: { label: "Explore Kore Coaching", to: "/memberships/kore-coached" },
    secondary: { label: "See Kore Access", to: "/memberships/kore-access" },
    bullets: [
      "2 coached small-group sessions every week",
      "Unlimited Kore classes",
      "Written weekly programme, updated monthly",
      "Nutrition & lifestyle guidance",
      "Monthly coach check-in",
    ],
  },
  access: {
    slug: "kore-access",
    name: "Kore Access",
    price: "From £39 per month",
    tag: "For self-motivated trainers",
    lead: "Based on your answers, Kore Access is the honest recommendation.",
    body: "You know how to train and you'd rather crack on. Kore Access gives you a calm, welcoming boutique gym in Bowness-on-Windermere — with none of the noise or intimidation of a commercial gym floor.",
    offer: { label: "Explore Kore Access", to: "/memberships/kore-access" },
    primary: { label: "Explore Kore Access", to: "/memberships/kore-access" },
    secondary: { label: "See Kore Coaching", to: "/memberships/kore-coached" },
    bullets: [
      "Full gym access during staffed hours",
      "£39/mo on 12-month membership · £49/mo rolling",
      "Welcoming, non-intimidating community",
      "Starter programme & welcome guide",
      "Nutrition guide",
    ],
  },
};

const track = (event, params = {}) => {
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", event, params);
  }
};

export default function HelpMeChoose() {
  const [step, setStep] = useState(0);
  const [tally, setTally] = useState({ coached: 0, access: 0 });
  const [answers, setAnswers] = useState([]);

  const total = QUESTIONS.length;
  const done = step >= total;
  const winner = tally.coached >= tally.access ? "coached" : "access"; // ties → coached
  const result = done ? RESULTS[winner] : null;

  const choose = (weight, index) => {
    const next = { ...tally, [weight]: tally[weight] + 1 };
    setTally(next);
    setAnswers([...answers, index]);
    track("help_me_choose_answer", { question: step + 1, weight });
    if (step + 1 >= total) {
      track("help_me_choose_result", { recommendation: (next.coached >= next.access ? "coached" : "access") });
    }
    setStep(step + 1);
  };

  const restart = () => { setStep(0); setTally({ coached: 0, access: 0 }); setAnswers([]); track("help_me_choose_restart"); };

  return (
    <div data-testid="help-me-choose-page">
      <Seo
        title="Help me choose — Kore Access or Kore Coaching?"
        description="A 3-question quiz to help you choose between Kore Access (£39/mo) and Kore Coaching (£149/mo). Honest recommendation based on how you like to train."
        path="/help-me-choose"
      />

      <section className="container-kore pt-24 pb-16 md:pt-28 max-w-3xl">
        <span className="eyebrow">Help me choose</span>
        <h1 className="display-1 mt-4 text-kore-forest">Which Kore membership is right for you?</h1>
        <p className="mt-5 text-kore-ink/70 leading-relaxed max-w-xl">
          Three quick questions. No email required. We'll recommend an honest fit — even if that's "not right now".
        </p>

        {/* Progress */}
        {!done && (
          <div className="mt-10 flex items-center gap-3">
            {QUESTIONS.map((_, i) => (
              <span
                key={i}
                className={`h-1 rounded-full flex-1 transition-all ${i < step ? "bg-kore-forest" : i === step ? "bg-kore-gold" : "bg-kore-ink/15"}`}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quiz */}
      <section className="container-kore pb-24 max-w-3xl">
        <AnimatePresence mode="wait">
          {!done ? (
            <motion.div
              key={`q-${step}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.35 }}
              data-testid={`quiz-step-${step + 1}`}
            >
              <div className="text-[11px] uppercase tracking-[0.22em] text-kore-gold">Question {step + 1} of {total}</div>
              <h2 className="display-2 mt-4 text-kore-forest">{QUESTIONS[step].q}</h2>
              <div className="mt-10 grid md:grid-cols-2 gap-4">
                {QUESTIONS[step].answers.map((a, i) => (
                  <button
                    key={i}
                    onClick={() => choose(a.weight, i)}
                    data-testid={`quiz-answer-${step + 1}-${i + 1}`}
                    className="text-left bg-white border border-kore-gold/25 rounded-3xl p-7 hover:border-kore-forest hover:shadow-lift transition-all group"
                  >
                    <div className="font-display text-2xl text-kore-forest leading-tight">{a.label}</div>
                    <div className="mt-6 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-kore-forest">
                      Choose <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              data-testid="quiz-result"
            >
              <div className="rounded-3xl bg-kore-forest text-kore-warm p-10 md:p-14 border-2 border-kore-light">
                <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-kore-light">
                  <Check size={13} /> {result.tag}
                </div>
                <h2 className="mt-5 display-1">{result.name}</h2>
                <div className="mt-2 text-kore-light">{result.price}</div>
                <p className="mt-6 text-lg text-kore-warm font-light leading-relaxed max-w-xl italic">{result.lead}</p>
                <p className="mt-4 text-kore-warm/80 leading-relaxed max-w-2xl">{result.body}</p>

                <ul className="mt-8 grid sm:grid-cols-2 gap-x-6 gap-y-3">
                  {result.bullets.map((b) => (
                    <li key={b} className="flex gap-3 items-start text-sm">
                      <Check size={15} className="text-kore-light mt-0.5 shrink-0" /> <span className="text-kore-warm/85">{b}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-10 flex flex-wrap gap-3">
                  <Link
                    to={result.offer.to}
                    state={result.offer.state}
                    data-testid="quiz-result-offer"
                    onClick={() => track("help_me_choose_offer_click", { recommendation: winner })}
                    className="btn-gold"
                  >
                    {result.offer.label} <ArrowRight size={14} />
                  </Link>
                  <Link
                    to={result.primary.to}
                    data-testid="quiz-result-primary"
                    onClick={() => track("help_me_choose_primary_click", { recommendation: winner })}
                    className="btn-ghost !text-kore-warm !border-kore-warm/40"
                  >
                    {result.primary.label} <ArrowUpRight size={13} />
                  </Link>
                </div>
              </div>

              <div className="mt-10 flex flex-wrap items-center justify-between gap-4">
                <Link to={result.secondary.to} className="text-sm text-kore-forest border-b border-kore-gold pb-1 hover:text-kore-gold transition">
                  Or, see {result.secondary.label.replace("See ", "")} instead
                </Link>
                <button onClick={restart} data-testid="quiz-restart" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-kore-ink/60 hover:text-kore-forest">
                  <RotateCcw size={12} /> Retake the quiz
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </section>
    </div>
  );
}
